/**
 * AdminInnerMirror — admin subsection surfacing Inner Mirror KPIs and
 * chapter response distributions. Reads `/api/inner-mirror/admin/overview`
 * which is gated by the same admin allow-list as the rest of the dashboard.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/hooks/useAuth";
import { PageHeader, KpiGrid, KpiCard, Panel, Table } from "../_shared";

const CHAPTER_LABELS = {
  1: "Discovery Motivation",
  2: "Emotional Impact",
  3: "Memorable Feature",
  4: "Experience Friction",
  5: "Feature Priority",
};

const pct = (x) => `${((x || 0) * 100).toFixed(1)}%`;

export default function AdminInnerMirror() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const { data: d } = await api.get("/inner-mirror/admin/overview");
        if (!dead) setData(d);
      } catch (e) {
        if (!dead) setErr(e?.response?.data?.detail || "load_failed");
      }
    })();
    return () => { dead = true; };
  }, []);

  if (err) return <Panel>Could not load Inner Mirror metrics: {String(err)}</Panel>;
  if (!data) return <Panel>Loading Inner Mirror…</Panel>;

  return (
    <div data-testid="admin-inner-mirror">
      <PageHeader title="Inner Mirror — Founding Devotees"
                   subtitle={`${data.founding_cohort_size}/${data.founding_limit} founding users enrolled.`} />
      <KpiGrid>
        <KpiCard testId="im-kpi-sessions" label="Sessions started"
                  value={data.sessions_total} />
        <KpiCard testId="im-kpi-completed" label="Completed"
                  value={data.completed} hint={pct(data.completion_rate)} />
        <KpiCard testId="im-kpi-rewards" label="Rewards claimed"
                  value={data.rewards_claimed} hint="500 credits each" />
        <KpiCard testId="im-kpi-shares" label="Share actions"
                  value={data.shares_started} />
        <KpiCard testId="im-kpi-acks" label="Acknowledgments"
                  value={data.acknowledgments} />
        <KpiCard testId="im-kpi-cohort" label="Founding cohort"
                  value={`${data.founding_cohort_size}/${data.founding_limit}`} />
      </KpiGrid>

      {[1, 2, 3, 4, 5].map((n) => {
        const rows = (data.chapter_distribution?.[n] || []).map((r) => ({
          selected: r.selected, count: r.count,
        }));
        return (
          <Panel key={n} title={`Chapter ${n} · ${CHAPTER_LABELS[n]}`}
                  testId={`im-ch${n}-panel`}>
            <Table
              testId={`im-ch${n}-table`}
              cols={[
                { label: "Selected", key: "selected" },
                { label: "Count", key: "count" },
              ]}
              rows={rows}
              empty="No answers yet."
              keyFn={(r) => r.selected}
            />
          </Panel>
        );
      })}
    </div>
  );
}
